/**
 * Canvelle customization: instant variant-media swap for the PDP gallery.
 *
 * Spec: reports/VARIANT-MEDIA-BUILD.md. This file is custom, so Horizon upgrades
 * keep it. It is loaded by snippets/canvelle-variant-media-map.liquid.
 *
 * HOW THE GALLERY UPDATES ON A SWATCH TAP - three layers, each a safety net for
 * the one above it:
 *
 *   1. Server (product-media-gallery-content.liquid) renders only the selected
 *      variant's media. Deep links and first paint are right with no JS at all.
 *   2. Horizon itself: variant-picker.js fetches the product page for the new
 *      variant and media-gallery.js replaces <media-gallery> with the server's
 *      markup. That markup is the AUTHORITY - videos, zoom, everything - and it
 *      always lands, typically a few hundred ms after the tap.
 *   3. This file: the moment the swatch changes, rebuild the visible gallery
 *      from the inline map so the shopper sees the new colorway immediately
 *      instead of waiting on the network. Layer 2 then replaces this interim.
 *
 * So this file is a latency optimization ONLY. Every failure mode is "do
 * nothing": all work happens on detached nodes and the live DOM is touched last.
 * If Horizon's markup changes in an upgrade and a selector below stops matching,
 * the interim silently stops and layer 2 still swaps the gallery correctly.
 *
 * During the interim every media is shown as its preview IMAGE (a video shows
 * its poster). The authoritative markup restores the real video slide.
 *
 * Deliberately no imports from Horizon's modules and no edits to Horizon's JS:
 * the only contract is the DOM (a `change` event inside <variant-picker>, and
 * the <media-gallery> markup the server just rendered).
 */

const MAP_SELECTOR = 'script[data-vm-map]';
const CONTAINER_SELECTOR = '.shopify-section, dialog';
const SLIDE_IMAGE_SELECTOR = 'img.product-media__image';

// Nodes built for a swap are cloned into a document with no browsing context and
// only join the page at the swap: Chrome downloads an <img> the moment it is cloned
// inside the live page, even one marked loading="lazy" — cloning the old gallery
// fetched every unviewed photo of the colour being left (HORIZON-THEME.md §1.21).
const INERT = document.implementation.createHTMLDocument('');
/** @template {Node} T @param {T} node @returns {T} */
const inertClone = (node) => /** @type {any} */ (INERT.importNode(node, true));

/** @type {WeakMap<Element, VariantMediaMap | null>} */
const parsedMaps = new WeakMap();

/** Warm-up images are kept referenced so the browser keeps them decoded. */
const warmImages = [];
const warmedMediaIds = new Set();

const debug = { lastSwap: null, lastHandoff: null, warmed: 0 };
window.__canvelleVariantMedia = debug;

/**
 * @typedef {object} MapMedia
 * @property {string} id
 * @property {string} u - Base image URL, without a width parameter.
 * @property {string} a - Alt text.
 * @property {number} r - Aspect ratio of the preview image.
 * @property {string} t - Media type.
 * @property {string} f - Focal point, or ''.
 * @property {boolean} [n] - True when not assigned to any variant.
 *
 * @typedef {object} MapVariant
 * @property {string} id
 * @property {string} fm - Featured media id, or ''.
 * @property {string[]} o - Option values, in option order.
 *
 * @typedef {object} VariantMediaMap
 * @property {boolean} hv
 * @property {boolean} ord - True when the lists are in display order (the app's own data).
 * @property {Record<string, string[]>} v
 * @property {Set<string>} common
 * @property {MapVariant[]} x
 * @property {MapMedia[]} m
 * @property {Map<string, MapMedia>} byId
 * @property {Set<string>} featured - Every variant's featured media id.
 */

/**
 * Rubik's compact metafield format (v: 2, September 2026) stores the assignment
 * per OPTION VALUE instead of per variant. This is a port of Rubik's own
 * rubikExpandVariantImages(): a variant's media = the list of its first
 * configured value (Color), in that order, minus anything missing from any of
 * its other non-empty lists (Size), minus the common media. The Liquid in
 * product-media-gallery-content.liquid does the same for the selected variant -
 * keep them in step.
 * @param {{values?: Array<{id: string, m?: string[]}>, variants?: Record<string, string[]>}} compact
 * @param {string[]} [common]
 * @returns {Record<string, string[]>}
 */
function expandCompact(compact, common) {
  const byId = new Map((compact.values || []).map((entry) => [String(entry.id), entry]));
  const isCommon = new Set((common || []).map(String));
  /** @type {Record<string, string[]>} */
  const perVariant = {};

  for (const [variantId, valueIds] of Object.entries(compact.variants || {})) {
    const lists = (Array.isArray(valueIds) ? valueIds : [])
      .map((valueId) => byId.get(String(valueId)))
      .filter((entry) => entry && Array.isArray(entry.m) && entry.m.length > 0)
      .map((entry) => /** @type {string[]} */ (entry.m).map(String));

    const others = lists.slice(1).map((list) => new Set(list));
    perVariant[variantId] = (lists[0] || []).filter(
      (mediaId) => others.every((set) => set.has(mediaId)) && !isCommon.has(mediaId)
    );
  }

  return perVariant;
}

/**
 * @param {Element} container
 * @returns {VariantMediaMap | null}
 */
function readMap(container) {
  const script = container.querySelector(MAP_SELECTOR);
  if (!script) return null;

  if (!parsedMaps.has(script)) {
    let map = null;
    try {
      const raw = JSON.parse(script.textContent || '');
      const lists = raw && (raw.v2 ? expandCompact(raw.v2, raw.c) : raw.v);
      if (raw && Array.isArray(raw.m) && Array.isArray(raw.x) && lists && typeof lists === 'object') {
        map = {
          hv: Boolean(raw.hv),
          ord: Boolean(raw.ord),
          v: lists,
          common: new Set((raw.c || []).map(String)),
          x: raw.x,
          m: raw.m,
          byId: new Map(raw.m.map((/** @type {MapMedia} */ media) => [media.id, media])),
          featured: new Set(raw.x.map((/** @type {MapVariant} */ variant) => variant.fm).filter(Boolean)),
        };
      }
    } catch (error) {
      console.warn('[canvelle-variant-media] map unreadable, instant swap off', error);
    }
    parsedMaps.set(script, map);
  }

  return parsedMaps.get(script) ?? null;
}

/**
 * The variant the picker now points at, resolved from the option values that
 * are checked right now (not from a data attribute, which goes stale when two
 * options are changed faster than Horizon's fetch returns).
 *
 * Returns null when the combination is not a variant (Navy + Large on the Hobo).
 * The rule for that case is that the gallery does not change at all - the photo
 * staying put is what sends the shopper's eye to "Unavailable" - so there is
 * nothing to do here, and assets/media-gallery.js declines Horizon's swap.
 * @param {Element} picker
 * @param {VariantMediaMap} map
 */
function resolveVariant(picker, map) {
  const controls = picker.querySelectorAll('fieldset input:checked, select');
  const values = Array.from(controls, (control) => /** @type {HTMLInputElement | HTMLSelectElement} */ (control).value);
  if (!values.length) return null;

  return (
    map.x.find((variant) => variant.o.length === values.length && variant.o.every((value, i) => value === values[i])) ??
    null
  );
}

/**
 * The media list for a variant, in display order. Mirrors the Liquid in
 * product-media-gallery-content.liquid exactly, so the interim and the
 * authoritative gallery show the same media in the same order.
 * @param {VariantMediaMap} map
 * @param {MapVariant} variant
 * @returns {MapMedia[] | null}
 */
function mediaFor(map, variant) {
  // Same rule as the server: the variant must be a KEY of the map. An empty list
  // still filters (a colorway with no photos assigned yet shows common + unassigned
  // media); a variant absent from the map is a stale configuration - leave it alone.
  const assigned = map.v[variant.id];
  if (!Array.isArray(assigned)) return null;
  const allow = new Set(assigned.map(String));
  const featured = variant.fm ? map.byId.get(variant.fm) : undefined;

  // The app's own data: the list IS the gallery order (filter 4 of 4 in
  // product-media-gallery-content.liquid). Other variants' featured images
  // still stay out (hide_variants); a featured image the list does not name
  // goes last, exactly as the server does it.
  if (map.ord) {
    const seen = new Set();
    const ordered = [];
    for (const id of assigned.map(String)) {
      const media = map.byId.get(id);
      if (!media || seen.has(id)) continue;
      if (map.hv && map.featured.has(id) && id !== variant.fm) continue;
      seen.add(id);
      ordered.push(media);
    }
    if (featured && !seen.has(featured.id)) ordered.push(featured);
    return ordered.length ? ordered : null;
  }

  const list = featured ? [featured] : [];

  for (const media of map.m) {
    if (featured) {
      if (media.id === featured.id) continue;
      // Mirrors the block's hide_variants setting: other variants' featured images stay out.
      if (map.hv && map.featured.has(media.id)) continue;
    }
    // media.n: Rubik has not assigned this media to any variant, so it shows on all of them.
    if (!allow.has(media.id) && !map.common.has(media.id) && !media.n) continue;
    list.push(media);
  }

  return list.length ? list : null;
}

/** @param {string | null} srcset */
function widthsOf(srcset) {
  return Array.from((srcset || '').matchAll(/\s(\d+)w(?:,|$)/g), (match) => Number(match[1]));
}

/** @param {string | null} url */
function widthParamOf(url) {
  const match = /[?&]width=(\d+)/.exec(url || '');
  return match ? Number(match[1]) : 0;
}

/**
 * Shopify lists every requested width whatever the original's size (verified on
 * 1000px-wide originals: all eight widths still render), so no capping here.
 * @param {MapMedia} media
 * @param {number[]} widths
 */
function srcsetFor(media, widths) {
  return widths.map((width) => `${media.u}&width=${width} ${width}w`).join(', ');
}

/**
 * Re-point a server-rendered <img> at another media. Everything is derived
 * from the image's own attributes (which widths, which src width, which sizes),
 * so the resulting URLs match what the server renders for that media.
 * @param {HTMLImageElement} img
 * @param {MapMedia} media
 */
function patchImage(img, media) {
  const widths = widthsOf(img.getAttribute('srcset'));
  const srcWidth = widthParamOf(img.getAttribute('src'));
  if (!srcWidth) throw new Error('image src has no width parameter');

  if (widths.length) img.setAttribute('srcset', srcsetFor(media, widths));
  img.setAttribute('src', `${media.u}&width=${srcWidth}`);
  img.setAttribute('alt', media.a || '');

  // Shopify's width/height attributes are the REQUESTED width and that width / ratio.
  img.setAttribute('width', String(srcWidth));
  img.setAttribute('height', String(Math.round(srcWidth / (media.r || 1))));

  if (img.hasAttribute('data_max_resolution')) {
    const maxWidth = widthParamOf(img.getAttribute('data_max_resolution')) || srcWidth;
    img.setAttribute('data_max_resolution', `${media.u}&width=${maxWidth}`);
  }

  img.style.removeProperty('object-position');
  if (img.matches(SLIDE_IMAGE_SELECTOR)) {
    img.style.setProperty('--focal-point', media.f || '');
  } else if (media.f) {
    img.style.setProperty('object-position', media.f);
  }
}

/**
 * "Slide 3 of 134" -> "Slide 1 of 11", whatever the locale's wording.
 * @param {string | null} label
 * @param {number} index
 * @param {number} total
 */
function relabel(label, index, total) {
  if (!label) return label;
  const count = (label.match(/\d+/g) || []).length;
  if (count < 2) return label;
  let seen = 0;
  return label.replace(/\d+/g, (number) => {
    seen += 1;
    if (seen === 1) return String(index + 1);
    if (seen === count) return String(total);
    return number;
  });
}

/**
 * @param {Element} element
 * @param {string} attribute
 * @param {number} index
 */
function reindex(element, attribute, index) {
  const value = element.getAttribute(attribute);
  if (value) element.setAttribute(attribute, value.replace(/\d+$/, String(index)));
}

/** @param {Element} slide */
function isPlainImageSlide(slide) {
  return (
    slide.matches('slideshow-slide.product-media-container--image') &&
    !slide.querySelector('deferred-media, product-model') &&
    slide.querySelector(SLIDE_IMAGE_SELECTOR) !== null
  );
}

/** Index of the tallest media, as the Liquid computes it (first strictly lowest ratio). */
function tallestIndex(/** @type {MapMedia[]} */ list) {
  let lowest = 50;
  let index = 0;
  list.forEach((media, i) => {
    if (media.r && media.r < lowest) {
      lowest = media.r;
      index = i;
    }
  });
  return index;
}

/**
 * A detached copy of the slideshow holding the new variant's slides, dots,
 * counter and thumbnails.
 * @param {Element} slideshow
 * @param {MapMedia[]} list
 */
function buildSlideshow(slideshow, list) {
  const fresh = /** @type {HTMLElement} */ (inertClone(slideshow));
  const scroller = fresh.querySelector('slideshow-slides');
  if (!scroller) throw new Error('no slideshow-slides');

  const protoSlide = Array.from(scroller.children).find(isPlainImageSlide);
  if (!protoSlide) throw new Error('no image slide to copy');

  // The server only marks a tallest slide for aspect_ratio: adapt.
  const marksTallest = scroller.querySelector('.product-media-container--tallest') !== null;
  const tallest = tallestIndex(list);
  const slides = list.map((media, i) => {
    const slide = /** @type {HTMLElement} */ (protoSlide.cloneNode(true));
    slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    slide.removeAttribute('reveal');
    slide.removeAttribute('hidden');
    slide.style.setProperty('--slideshow-timeline', `--slide-${i}`);
    slide.classList.toggle('product-media-container--tallest', marksTallest && i === tallest);
    reindex(slide, 'on:click', i);

    const wrapper = /** @type {HTMLElement | null} */ (slide.querySelector('.product-media'));
    const img = /** @type {HTMLImageElement | null} */ (slide.querySelector(SLIDE_IMAGE_SELECTOR));
    if (!wrapper || !img) throw new Error('slide markup changed');
    wrapper.dataset.mediaId = media.id;
    wrapper.style.setProperty('--ratio', String(media.r || 1));

    // Same rule as the server-rendered gallery (HORIZON-THEME.md §1.21): photos 1-2
    // load now, 3+ when assets/canvelle-gallery-lazy.js sees the shopper use the
    // gallery. Set BEFORE patchImage gives the image its new src: an eager image
    // starts downloading the moment it has a src, even while still detached.
    if (i < 2) img.removeAttribute('loading');
    else img.setAttribute('loading', 'lazy');
    patchImage(img, media);
    img.setAttribute('fetchpriority', i === 0 ? 'high' : 'auto');
    // The first image was warmed in the background; paint it with the swap, not a frame later.
    if (i === 0) img.setAttribute('decoding', 'sync');
    return slide;
  });
  scroller.replaceChildren(...slides);

  fresh.style.setProperty('--slideshow-timeline', list.map((_, i) => `--slide-${i}`).join(', '));
  fresh.setAttribute('initial-slide', '0');
  for (const attribute of ['actioned', 'transitioning', 'refreshing-timeline', 'paused', 'in-viewport']) {
    fresh.removeAttribute(attribute);
  }

  for (const dots of fresh.querySelectorAll('.slideshow-controls__dots')) {
    const protoDot = dots.firstElementChild;
    if (!protoDot) continue;
    dots.replaceChildren(
      ...list.map((_, i) => {
        const dot = /** @type {HTMLElement} */ (protoDot.cloneNode(true));
        const button = dot.querySelector('button');
        if (!button) throw new Error('dot markup changed');
        button.style.setProperty('animation-timeline', `--slide-${i}`);
        button.setAttribute('aria-label', relabel(button.getAttribute('aria-label'), i, list.length) || '');
        reindex(button, 'on:click', i);
        button.toggleAttribute('aria-current', false);
        if (i === 0) button.setAttribute('aria-current', 'true');
        button.textContent = String(i + 1);
        return dot;
      })
    );
  }

  for (const counter of fresh.querySelectorAll('.slideshow-controls__counter')) {
    const current = counter.querySelector('[ref="current"]');
    if (current) current.textContent = '1';
    if (counter.lastChild?.nodeType === Node.TEXT_NODE) counter.lastChild.textContent = String(list.length);
  }

  for (const rail of fresh.querySelectorAll('.slideshow-controls__thumbnails')) {
    const protoThumb = Array.from(rail.children).find((thumb) => !thumb.querySelector('.slideshow-controls__thumbnail-badge'));
    const badge = rail.querySelector('.slideshow-controls__thumbnail-badge');
    if (!protoThumb) continue;
    rail.replaceChildren(
      ...list.map((media, i) => {
        const thumb = /** @type {HTMLElement} */ (protoThumb.cloneNode(true));
        const img = thumb.querySelector('img');
        if (!img) throw new Error('thumbnail markup changed');
        thumb.setAttribute('aria-label', relabel(thumb.getAttribute('aria-label'), i, list.length) || '');
        reindex(thumb, 'on:click', i);
        thumb.style.setProperty('--aspect-ratio', String(media.r || 1));
        thumb.toggleAttribute('aria-current', false);
        if (i === 0) thumb.setAttribute('aria-current', 'true');
        patchImage(img, media);
        if (badge && media.t !== 'image') thumb.append(badge.cloneNode(true));
        return thumb;
      })
    );
  }

  return fresh;
}

/**
 * Detached replacement children for the zoom dialog, or null when the dialog
 * is absent or its markup is not what we expect.
 * @param {Element} gallery
 * @param {MapMedia[]} list
 */
function buildZoom(gallery, list) {
  const zoomList = gallery.querySelector('zoom-dialog .dialog-zoomed-gallery');
  const thumbList = gallery.querySelector('zoom-dialog .dialog-thumbnails-list');
  if (!zoomList) return null;

  const protoItem = Array.from(zoomList.children).find(
    (item) => item.matches('.product-media-container--image') && !item.querySelector('deferred-media, product-model')
  );
  if (!protoItem) return null;

  const items = list.map((media, i) => {
    const item = /** @type {HTMLElement} */ (inertClone(protoItem));
    const wrapper = /** @type {HTMLElement | null} */ (item.querySelector('.product-media'));
    const img = /** @type {HTMLImageElement | null} */ (item.querySelector(SLIDE_IMAGE_SELECTOR));
    if (!wrapper || !img) throw new Error('zoom markup changed');
    item.id = `product-${media.id}-${i + 1}`;
    if (item.style.getPropertyValue('--media-preview-ratio')) {
      item.style.setProperty('--media-preview-ratio', String(media.r || 1));
    }
    wrapper.dataset.mediaId = media.id;
    wrapper.style.setProperty('--ratio', String(media.r || 1));
    patchImage(img, media);
    return item;
  });

  let thumbs = null;
  const protoThumb = thumbList?.firstElementChild;
  if (thumbList && protoThumb && list.length > 1) {
    thumbs = list.map((media, i) => {
      const thumb = /** @type {HTMLElement} */ (inertClone(protoThumb));
      const img = thumb.querySelector('img');
      if (!img) throw new Error('zoom thumbnail markup changed');
      reindex(thumb, 'on:click', i);
      reindex(thumb, 'on:pointerenter', i);
      thumb.style.setProperty('--aspect-ratio', String(media.r || 1));
      thumb.style.setProperty('--gallery-aspect-ratio', String(media.r || 1));
      thumb.toggleAttribute('aria-current', false);
      if (i === 0) thumb.setAttribute('aria-current', 'true');
      patchImage(img, media);
      return thumb;
    });
  }

  return { zoomList, items, thumbList, thumbs };
}

/** Galleries already being watched for their hand-off. */
const watchedGalleries = new WeakSet();

/**
 * Horizon replaces <media-gallery> when its fetch lands, which would send a
 * shopper who has already swiped to photo 4 back to photo 1. Carry the position
 * over - only when the incoming gallery is for the same variant as the interim.
 * @param {HTMLElement} gallery - The gallery holding the interim.
 */
function keepSlideAcrossHandoff(gallery) {
  const parent = gallery.parentElement;
  if (!parent || watchedGalleries.has(gallery)) return;
  watchedGalleries.add(gallery);

  const observer = new MutationObserver(() => {
    if (gallery.isConnected) return;
    observer.disconnect();

    const next = /** @type {HTMLElement | null} */ (parent.querySelector('media-gallery'));
    if (!next || next.dataset.vmVariant !== gallery.dataset.vmVariant) return;

    const previous = /** @type {any} */ (gallery.querySelector('slideshow-component'));
    const index = Number(previous?.current) || 0;
    if (index < 1) return;

    const slideshow = /** @type {any} */ (next.querySelector('slideshow-component'));
    if (typeof slideshow?.select !== 'function') return;
    if (index >= next.querySelectorAll('slideshow-slide').length) return;

    // Once now (before the browser paints the new gallery) and once after
    // Horizon's own first-frame setup, which can otherwise settle on slide 0.
    const restore = () => slideshow.isConnected && slideshow.select(index, undefined, { animate: false });
    restore();
    requestAnimationFrame(() => requestAnimationFrame(restore));
    debug.lastHandoff = { variant: next.dataset.vmVariant, restoredIndex: index };
  });

  observer.observe(parent, { childList: true });
  // Horizon aborts a fetch that is overtaken by another tap; never watch forever.
  setTimeout(() => observer.disconnect(), 60000);
}

/**
 * Show the picked variant's media right now.
 * @param {Element} container
 * @param {Element} picker
 */
function swap(container, picker) {
  const startedAt = performance.now();
  const map = readMap(container);
  if (!map) return;

  const gallery = /** @type {HTMLElement | null} */ (container.querySelector('media-gallery'));
  const slideshow = gallery?.querySelector('slideshow-component');
  if (!gallery || !slideshow) return;

  const variant = resolveVariant(picker, map);
  // Not a variant (a size this color does not come in): the gallery stays as it is.
  if (!variant) return;
  if (gallery.dataset.vmVariant === variant.id) return;

  const list = mediaFor(map, variant);
  if (!list) return;
  // A one-photo gallery is rendered without controls, so its markup differs from a
  // carousel's. Swap like for like; leave one <-> many to Horizon's own update.
  const currentCount = slideshow.querySelectorAll('slideshow-slide').length;
  if (currentCount > 1 !== list.length > 1) return;

  let fresh;
  let zoom = null;
  try {
    fresh = buildSlideshow(slideshow, list);
    try {
      zoom = buildZoom(gallery, list);
    } catch (error) {
      zoom = null;
    }
    if (!zoom) {
      // The zoom dialog still holds the previous variant: keep it shut until the real markup lands.
      for (const slide of fresh.querySelectorAll('slideshow-slide')) slide.removeAttribute('on:click');
    }
  } catch (error) {
    console.warn('[canvelle-variant-media] instant swap skipped, Horizon will update the gallery', error);
    debug.lastSwap = { variant: variant.id, ok: false, reason: String(error) };
    return;
  }

  // Everything above ran on detached nodes. Only now touch the page.
  slideshow.replaceWith(fresh);
  if (zoom) {
    zoom.zoomList.replaceChildren(...zoom.items);
    if (zoom.thumbList && zoom.thumbs) zoom.thumbList.replaceChildren(...zoom.thumbs);
  }
  gallery.dataset.vmVariant = variant.id;
  gallery.dataset.vmInterim = 'true';
  keepSlideAcrossHandoff(gallery);

  debug.lastSwap = {
    variant: variant.id,
    ok: true,
    slides: list.length,
    zoom: Boolean(zoom),
    ms: Math.round((performance.now() - startedAt) * 10) / 10,
  };
}

/**
 * Warm the FIRST image of every other variant, one at a time, so the frame
 * after a swatch tap is already downloaded and decoded. One image per variant
 * is the point - the rest stream in as the shopper moves through the carousel.
 * @param {Element} container
 */
async function warm(container) {
  const connection = /** @type {any} */ (navigator).connection;
  if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) return;

  const map = readMap(container);
  const gallery = /** @type {HTMLElement | null} */ (container.querySelector('media-gallery'));
  const protoImg = gallery?.querySelector(`slideshow-slide ${SLIDE_IMAGE_SELECTOR}`);
  if (!map || !gallery || !protoImg) return;

  const widths = widthsOf(protoImg.getAttribute('srcset'));
  const sizes = protoImg.getAttribute('sizes');
  if (!widths.length || !sizes) return;

  const current = map.x.find((variant) => variant.id === gallery.dataset.vmVariant);
  const differences = (/** @type {MapVariant} */ variant) =>
    current ? variant.o.filter((value, i) => value !== current.o[i]).length : 0;

  // Nearest first: a different color in the same size before a different size.
  const queue = map.x.filter((variant) => variant !== current).sort((a, b) => differences(a) - differences(b));

  for (const variant of queue) {
    const first = mediaFor(map, variant)?.[0];
    if (!first || warmedMediaIds.has(first.id)) continue;
    warmedMediaIds.add(first.id);

    // Same sizes + srcset as the real slide, so the browser picks (and caches) the same file.
    const image = new Image();
    image.sizes = sizes;
    image.srcset = srcsetFor(first, widths);
    image.src = `${first.u}&width=${widths[widths.length - 1]}`;
    warmImages.push(image);

    try {
      // decode() does not settle while the tab is hidden; never let one image hold up the queue.
      await Promise.race([image.decode(), new Promise((resolve) => setTimeout(resolve, 4000))]);
    } catch (error) {
      // A failed warm-up costs nothing; the swap simply loads that image on demand.
    }
    debug.warmed += 1;
    await new Promise((resolve) => whenIdle(resolve));
  }
}

/** @param {() => void} callback */
function whenIdle(callback) {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => callback(), { timeout: 3000 });
  } else {
    setTimeout(callback, 200);
  }
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const picker = target.closest('variant-picker');
  if (!picker || target.closest('product-card')) return;

  const container = picker.closest(CONTAINER_SELECTOR);
  if (!container) return;

  try {
    swap(container, picker);
  } catch (error) {
    console.warn('[canvelle-variant-media] instant swap failed, Horizon will update the gallery', error);
  }
});

function startWarming() {
  // A product opened in a background tab warms up when the shopper actually looks at it.
  if (document.visibilityState !== 'visible') {
    document.addEventListener('visibilitychange', startWarming, { once: true });
    return;
  }

  whenIdle(() => {
    for (const script of document.querySelectorAll(MAP_SELECTOR)) {
      const container = script.closest(CONTAINER_SELECTOR);
      if (container) warm(container).catch(() => {});
    }
  });
}

if (document.readyState === 'complete') {
  startWarming();
} else {
  window.addEventListener('load', startWarming, { once: true });
}
